/**
 * CEO Command Center - Common Components
 * 
 * Shared components used across the application:
 * - ErrorBoundary: Catches and displays React errors
 * - LoadingState: Loading indicators and skeletons
 * - EmptyState: Empty data state displays
 * - ConfirmDialog: Confirmation modals
 * 
 * @module components/common
 * @version 10.0.0
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Spinner, Skeleton, Modal } from '../ui';
import { cn } from '../../utils/format';

// ERROR BOUNDARY

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary component
 * Catches JavaScript errors in child components and displays fallback UI
 * 
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }
  
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <div className="text-6xl mb-4">😵</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-500 mb-6 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={this.handleRetry}>
              Try Again
            </Button>
            <Button variant="primary" onClick={this.handleReload}>
              Reload Page
            </Button>
          </div>
          {import.meta.env.DEV && this.state.error && (
            <details className="mt-6 text-left w-full max-w-lg">
              <summary className="text-sm text-gray-500 cursor-pointer">
                Error Details (Development Only)
              </summary>
              <pre className="mt-2 p-4 bg-gray-100 rounded-lg text-xs overflow-auto">
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }
    
    return this.props.children;
  }
}

// LOADING STATE

interface LoadingStateProps {
  message?: string;
  fullScreen?: boolean;
  variant?: 'spinner' | 'skeleton' | 'dots';
  className?: string;
}

/**
 * Loading state component
 * Shows loading indicator with optional message
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  fullScreen = false,
  variant = 'spinner',
  className,
}) => {
  const content = (
    <div className={cn('flex flex-col items-center justify-center p-8', className)}>
      {variant === 'spinner' && <Spinner size="lg" />}
      {variant === 'dots' && (
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      )}
      {message && (
        <p className="mt-4 text-gray-500 text-sm">{message}</p>
      )}
    </div>
  );
  
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }
  
  return content;
};

/**
 * Task card skeleton for loading states
 */
export const TaskCardSkeleton: React.FC = () => (
  <div className="p-4 border rounded-lg bg-white animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
    <div className="flex gap-2 mt-3">
      <Skeleton className="h-5 w-20 rounded-full" />
      <Skeleton className="h-5 w-24 rounded-full" />
    </div>
  </div>
);

/**
 * Signal card skeleton for loading states
 */
export const SignalCardSkeleton: React.FC = () => (
  <div className="p-4 border-l-4 border-l-gray-200 rounded-lg bg-gray-50 animate-pulse">
    <div className="flex justify-between mb-2">
      <Skeleton className="h-7 w-24" />
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
    <Skeleton className="h-5 w-3/4 mb-2" />
    <Skeleton className="h-4 w-1/2 mb-4" />
    <div className="flex gap-2">
      <Skeleton className="h-8 w-20 rounded-lg" />
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  </div>
);

/**
 * Metrics skeleton for loading states
 */
export const MetricsSkeleton: React.FC = () => (
  <div className="grid grid-cols-5 gap-4">
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="p-4 bg-white rounded-lg border animate-pulse">
        <Skeleton className="h-4 w-20 mb-2" />
        <Skeleton className="h-7 w-16 mb-1" />
        <Skeleton className="h-4 w-12" />
      </div>
    ))}
  </div>
);

/**
 * List skeleton for loading states
 */
export const ListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <TaskCardSkeleton key={i} />
    ))}
  </div>
);

// EMPTY STATE

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * Empty state component
 * Shows when there's no data to display
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '📭',
  title,
  description,
  action,
  className,
}) => {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center py-12 px-4 text-center',
      className
    )}>
      <div className="text-5xl mb-4">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {description && (
        <p className="text-gray-500 text-sm mb-6 max-w-md">{description}</p>
      )}
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
};

/**
 * Preset empty states for common scenarios
 * All are proper React components that accept props
 */
export const EmptyStates = {
  NoSignals: () => (
    <EmptyState
      icon="✨"
      title="All clear - no urgent signals"
      description="This is a good sign! Your team is on track. Check back later for updates."
    />
  ),

  NoTasks: ({ onAdd }: { onAdd?: () => void }) => (
    <EmptyState
      icon="📋"
      title="No tasks yet"
      description="Create your first task to start tracking work."
      action={onAdd ? { label: 'Add Task', onClick: onAdd } : undefined}
    />
  ),

  NoDeals: ({ onAdd }: { onAdd?: () => void }) => (
    <EmptyState
      icon="💼"
      title="Pipeline is empty"
      description="Add deals to start tracking your sales pipeline."
      action={onAdd ? { label: 'Add Deal', onClick: onAdd } : undefined}
    />
  ),

  NoResults: ({ onClear }: { onClear?: () => void }) => (
    <EmptyState
      icon="🔍"
      title="No results found"
      description="Try adjusting your search or filters."
      action={onClear ? { label: 'Clear Filters', onClick: onClear } : undefined}
    />
  ),

  Error: ({ onRetry }: { onRetry?: () => void }) => (
    <EmptyState
      icon="⚠️"
      title="Failed to load data"
      description="Something went wrong. Please try again."
      action={onRetry ? { label: 'Retry', onClick: onRetry } : undefined}
    />
  ),
};

// CONFIRM DIALOG

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

/**
 * Confirmation dialog component
 * Used for destructive actions that need user confirmation
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}) => {
  const icons = {
    danger: '⚠️',
    warning: '⚡',
    info: 'ℹ️',
  };
  
  const buttonVariants = {
    danger: 'danger' as const,
    warning: 'primary' as const,
    info: 'primary' as const,
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" showClose={false}>
      <div className="text-center">
        <div className="text-4xl mb-4">{icons[variant]}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-center">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button 
            variant={buttonVariants[variant]} 
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// INLINE LOADING

interface InlineLoadingProps {
  message?: string;
}

/**
 * Inline loading indicator for buttons or small areas
 */
export const InlineLoading: React.FC<InlineLoadingProps> = ({ message }) => (
  <span className="inline-flex items-center gap-2 text-sm text-gray-500">
    <Spinner size="sm" />
    {message && <span>{message}</span>}
  </span>
);

// PAGE HEADER

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumb?: Array<{ label: string; onClick?: () => void }>;
}

/**
 * Page header with title, subtitle, and actions
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  breadcrumb,
}) => {
  return (
    <div className="mb-6">
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          {breadcrumb.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span>/</span>}
              {item.onClick ? (
                <button
                  onClick={item.onClick}
                  className="hover:text-gray-700 hover:underline"
                >
                  {item.label}
                </button>
              ) : (
                <span className="text-gray-900">{item.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-gray-500">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

// SECTION

interface SectionProps {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

/**
 * Section component for grouping content
 */
export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  actions,
  children,
  className,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  
  return (
    <section className={cn('mb-6', className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {collapsible && (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <span className={cn(
                  'inline-block transition-transform',
                  collapsed ? '' : 'rotate-90'
                )}>
                  ▶
                </span>
              </button>
            )}
            <div>
              {title && (
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              )}
              {subtitle && (
                <p className="text-sm text-gray-500">{subtitle}</p>
              )}
            </div>
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      {!collapsed && children}
    </section>
  );
};

// STAT CARD

interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: ReactNode;
  highlight?: boolean;
  className?: string;
}

/**
 * Stat card for displaying metrics
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtitle,
  change,
  trend,
  trendValue,
  icon,
  highlight = false,
  className,
}) => {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-500',
  };
  
  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→',
  };
  
  return (
    <div className={cn(
      'p-4 rounded-lg border bg-white',
      highlight && 'bg-amber-50 border-amber-200',
      className
    )}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-500">{label}</span>
        {icon && <span className="text-gray-400">{icon}</span>}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        {highlight && <span className="text-amber-500">⚠️</span>}
        {trendValue && trend && (
          <span className={cn('text-sm', trendColors[trend])}>
            {trendIcons[trend]} {trendValue}
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      )}
      {change && (
        <div className={cn('text-sm mt-1', trend ? trendColors[trend] : 'text-gray-500')}>
          {trend && trendIcons[trend]} {change}
        </div>
      )}
    </div>
  );
};

// ============================================
// RE-EXPORTS FROM SEPARATE FILES
// ============================================

export { ConnectionStatus, ConnectionErrorBanner } from './ConnectionStatus';
export { ProfileDropdown } from './ProfileDropdown';
export { SyncStatus } from './SyncStatus';
export { SpaceInviteDialog, JoinSpaceDialog } from './SpaceInvite';
export { SyncSettingsPanel } from './SyncSettings';
export { FileDropZone } from './FileDropZone';
export type { ParsedTranscript, TranscriptSegment, ActionItem, DataFileResult } from './FileDropZone';
export { default as ColumnMappingModal } from './ColumnMappingModal';
export type { ColumnMappingModalProps } from './ColumnMappingModal';

